import {ABILITY_CAST_COLORS as COLORS, ABILITY_CAST_TEXTURE_KEYS, GOBLINS_BOMB_CAST, GOBLINS_TRAP_CAST, SEDUCTION_CAST} from '../../configs/abilityCastAnimation';
import {COMBAT_CAST_COLORS as FX} from '../../configs/combatCastAnimation';
import {
  FACTION_CAST_CHARGE as CHARGE,
  FACTION_CAST_COLORS as FACTION,
  GOBLINS_BOMB_RICH_CAST,
  GOBLINS_TRAP_RICH_CAST,
  LEPRECHAUNS_RICH_CAST,
  SEDUCTION_RICH_CAST,
} from '../../configs/factionAbilityCast';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {HEX_WAVE_TEXTURE_KEYS} from '../../configs/unitRecruitmentAnimation';
import {
  buildGoblinsBombCastPlan,
  buildGoblinsHiddenTrapCastPlan,
  buildGoblinsTrapCastPlan,
  buildLeprechaunsTheftCastPlan,
  buildSeductionCastPlan,
  clamp01,
  createElement,
  easeInOutSine,
  easeOutCubic,
  easeOutQuad,
  fadeInOut,
  GOBLINS_BOMB_OUTCOMES,
  lerp,
  pushRisingSpiral,
  resolveArcLift,
  resolveArcPoint,
} from './buildAbilityCast';
import {pushFlash, pushHexWave, pushPuff, pushStreak} from './buildCombatCast';
import {HEX_WAVE_TEXTURE_SIZE, LIGHT_BEAM_ASPECT, resolveHexWaveScale} from './hexWaveTextures';

/**
 * НАСЫЩЕННЫЕ ПЛАНЫ СПОСОБНОСТЕЙ ФРАКЦИЙ (см. configs/factionAbilityCast): основной план
 * из buildAbilityCast плюс слои того же языка, что у найма и постройки: знаки на земле,
 * шестиугольные волны, лучи и след кометы. Момент попадания остается у основного плана.
 */

const TWO_PI = Math.PI * 2;

/** Основной план с новыми слоями: он становится длиннее, только если слои идут дольше */
const withLayers = (plan, layers, shake) => {
  const elements = [...plan.elements, ...layers];
  return {
    ...plan,
    elements,
    totalMs: elements.reduce((latest, element) => Math.max(latest, element.endMs), plan.totalMs),
    shake: shake ?? plan.shake,
  };
};

const resolveHexSizePx = (hexSize, radiusFactor = 1) => HEX_WAVE_TEXTURE_SIZE * resolveHexWaveScale(hexSize * radiusFactor);

/** Штриховой шестиугольник на земле: появляется, вращается и гаснет */
const pushRune = (elements, {at, hexSize, tint, radiusFactor, turn, startMs = 0, durationMs, alpha, hexRotation = 0, order = 1}) => {
  elements.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED, tint, sizePx: resolveHexSizePx(hexSize, radiusFactor),
    startMs, durationMs, layer: 'ground', order,
    pose: (local) => ({
      ...at,
      scale: lerp(1.25, 1, easeOutCubic(clamp01(local * 2.5))),
      rotation: hexRotation + turn * local,
      alpha: alpha * fadeInOut(local, 0.2, 0.25),
    }),
  }));
};

/** Штриховой знак сходится на цели и держится до момента удара */
const pushClosingSight = (elements, {at, hexSize, tint, sight, impactMs, hexRotation = 0, tailMs = 140}) => {
  const startMs = Math.max(0, impactMs - sight.leadMs);
  const closingMs = impactMs - startMs;
  elements.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED, tint, sizePx: resolveHexSizePx(hexSize),
    startMs, durationMs: closingMs + tailMs, layer: 'ground', order: 2,
    pose: (local, elapsedMs) => {
      const share = clamp01((elapsedMs - startMs) / closingMs);
      return {
        ...at,
        scale: lerp(sight.fromFactor, sight.toFactor, easeOutCubic(share)),
        rotation: hexRotation + sight.turn * (1 - share),
        alpha: sight.alpha * clamp01(share * 3) * (1 - clamp01((elapsedMs - impactMs) / tailMs)),
      };
    },
  }));
};

/** Волны по списку из конфига */
const pushWaves = (elements, {at, hexSize, waves, colors, startMs, hexRotation}) => {
  waves.forEach((wave, index) => {
    pushHexWave(elements, {
      at, hexSize, color: colors[index % colors.length], endFactor: wave.endFactor, durationMs: wave.durationMs,
      alpha: wave.alpha, startMs: startMs + wave.delayMs, hexRotation, order: 1 + index,
    });
  });
};

/** Лучи за отрядом: на земле, чтобы отряд оставался читаем */
const pushRays = (elements, {at, size, rays, tint, startMs}) => {
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint, sizePx: size * rays.sizeFactor,
    startMs, durationMs: rays.durationMs, layer: 'ground', order: 3,
    pose: (local) => ({
      ...at,
      scale: 0.3 + 0.7 * easeOutCubic(clamp01(local * 2.5)),
      alpha: rays.alpha * fadeInOut(local, 0.1, 0.55),
      rotation: rays.turn * local,
    }),
  }));
};

/** Шестиугольник ячейки вспыхивает: контур и светлая линия */
const pushHexFlare = (elements, {at, hexSize, tint, lightTint, startMs, durationMs, alpha, hexRotation = 0, order = 4, pulses = 0, sizeFactor = 1}) => {
  [
    {textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint, order},
    {textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: lightTint, order: order + 0.5},
  ].forEach((layer) => {
    elements.push(createElement({
      ...layer, sizePx: resolveHexSizePx(hexSize, sizeFactor), startMs, durationMs,
      pose: (local) => ({
        ...at,
        rotation: hexRotation,
        alpha: alpha * (1 - local) * (pulses > 0 ? 0.55 + 0.45 * Math.cos(local * TWO_PI * pulses) : 1),
      }),
    }));
  });
};

/**
 * Отряд колдует: поле вокруг темнеет, на земле вращается знак, кольца сходятся к моменту
 * запуска, и в этот момент бьют вспышка и лучи
 */
const pushCharge = (elements, {anchor, size, color, lightColor, launchMs, endMs, hexRotation, rng}) => {
  const {dim, rune, rings, launch} = CHARGE;
  const at = anchor.center || anchor.body;
  const ground = anchor.ground || at;
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.black, sizePx: size * dim.sizeFactor,
    startMs: 0, durationMs: endMs, order: -10,
    pose: (local) => ({...at, alpha: dim.alpha * fadeInOut(local, 0.25, 0.35)}),
  }));
  pushRune(elements, {at, hexSize: size, tint: color, radiusFactor: rune.radiusFactor, turn: rune.turn, durationMs: endMs, alpha: rune.alpha, hexRotation});
  pushRune(elements, {at, hexSize: size, tint: lightColor, radiusFactor: rune.innerRadiusFactor, turn: -rune.turn, durationMs: endMs, alpha: rune.alpha, hexRotation, order: 2});

  const ringLifeMs = launchMs * rings.lifeShare;
  for (let index = 0; index < rings.count; index += 1) {
    const startMs = (launchMs - ringLifeMs) * (index / Math.max(1, rings.count - 1));
    elements.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: index % 2 ? lightColor : color, sizePx: resolveHexSizePx(size),
      startMs, durationMs: ringLifeMs, layer: 'ground', order: 3,
      pose: (local) => ({
        ...at,
        scale: lerp(rings.fromFactor, 1, local * local),
        rotation: hexRotation,
        alpha: clamp01(local / 0.3) * (local > 0.88 ? (1 - local) / 0.12 : 1),
      }),
    }));
  }

  pushFlash(elements, {at: anchor.body || at, tint: COLORS.white, sizePx: size * launch.sizeFactor, startMs: launchMs, durationMs: launch.durationMs, order: 9, blend: 'add'});
  pushRays(elements, {at: ground, size, rays: {sizeFactor: launch.raysSizeFactor, durationMs: launch.raysMs, turn: 0.5 + rng() * 0.3, alpha: launch.raysAlpha}, tint: color, startMs: launchMs});
};

/** Хвост кометы: сплошная полоса света за летящим снарядом, тает быстро */
const pushComet = (elements, {pointAt, startMs, durationMs, size, tints}) => {
  const {count, lifeMs, fromSizeFactor, toSizeFactor} = CHARGE.comet;
  for (let index = 0; index < count; index += 1) {
    const share = index / count;
    const origin = pointAt(share);
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: tints[index % tints.length], sizePx: size * lerp(toSizeFactor, fromSizeFactor, share),
      startMs: startMs + share * durationMs, durationMs: lifeMs, order: 2,
      pose: (local) => ({...origin, scale: 1 - 0.65 * local, alpha: 0.85 * (1 - local)}),
    }));
  }
};

/** Искры-осколки по кругу */
const pushRadialStreaks = (elements, {at, size, streaks, tints, startMs, count = streaks.count, reachScale = 1, rng, order = 10}) => {
  for (let index = 0; index < count; index += 1) {
    const angle = index / count * TWO_PI + (rng() - 0.5) * 0.45;
    const reach = streaks.reachFactor ?? lerp(streaks.minReachFactor, streaks.maxReachFactor, rng());
    pushStreak(elements, {
      at, angle, distance: size * reach * reachScale, lengthPx: size * streaks.lengthFactor, thicknessPx: size * streaks.thicknessFactor,
      tint: tints[index % tints.length], startMs, lifeMs: streaks.lifeMs, order,
    });
  }
};

const flightOf = (from, to, size, flight) => {
  const lift = resolveArcLift(Math.hypot(to.x - from.x, to.y - from.y), size, flight);
  return (share) => resolveArcPoint(from, to, lift, easeInOutSine(clamp01(share)));
};

/**
 * СОБЛАЗН СУККУБОВ: ритуал вместо простого броска сердца
 *
 * @param {Object} params — как у buildSeductionCastPlan
 * @param {number} [params.hexRotation] — поворот шестиугольников под вершину ячейки
 */
export const buildSeductionRichCastPlan = ({attacker, target, hexSize, success, hexRotation = 0, rng = Math.random}) => {
  const plan = buildSeductionCastPlan({attacker, target, hexSize, success, rng});
  const {flight} = SEDUCTION_CAST;
  const {lure, plumes, success: win, failure: lose} = SEDUCTION_RICH_CAST;
  const size = hexSize;
  const {impactMs} = plan;
  const to = target.body;
  const targetAt = target.center || to;
  const targetGround = target.ground || to;
  const flightPoint = flightOf(attacker.body, to, size, flight);
  const layers = [];

  pushCharge(layers, {
    anchor: attacker, size, color: FACTION.infernal, lightColor: COLORS.heart, launchMs: flight.startMs, endMs: impactMs, hexRotation, rng,
  });
  pushClosingSight(layers, {at: targetAt, hexSize: size, tint: COLORS.heart, sight: lure, impactMs, hexRotation});
  pushComet(layers, {pointAt: flightPoint, startMs: flight.startMs, durationMs: flight.durationMs, size, tints: [FACTION.infernal, COLORS.heart]});
  // Темный дым тянется за сердцем: это не подарок, а чары
  for (let index = 0; index < plumes.count; index += 1) {
    const spawnMs = flight.startMs + index * plumes.everyMs;
    if (spawnMs < impactMs) {
      pushPuff(layers, {
        at: flightPoint((spawnMs - flight.startMs) / flight.durationMs), tint: FACTION.plum, sizePx: size * plumes.sizeFactor,
        startMs: spawnMs, lifeMs: plumes.lifeMs, alpha: plumes.alpha, riseY: size * 0.28, spin: (rng() - 0.5) * 1.2, order: 1,
      });
    }
  }

  if (!success) {
    pushHexFlare(layers, {
      at: targetAt, hexSize: size, tint: FACTION.shieldDeep, lightTint: FACTION.shield, startMs: impactMs,
      durationMs: lose.shield.durationMs, alpha: lose.shield.alpha, hexRotation, sizeFactor: lose.shield.sizeFactor,
    });
    pushHexWave(layers, {at: targetAt, hexSize: size, color: FACTION.shieldDeep, endFactor: lose.wave.endFactor, durationMs: lose.wave.durationMs, alpha: lose.wave.alpha, startMs: impactMs, hexRotation});
    pushRadialStreaks(layers, {at: to, size, streaks: lose.shards, tints: [FACTION.shield, COLORS.failGray], startMs: impactMs, rng});
    return withLayers(plan, layers);
  }

  pushFlash(layers, {at: to, tint: COLORS.white, sizePx: size * win.flash.sizeFactor, startMs: impactMs, durationMs: win.flash.durationMs, order: 9, blend: 'add'});
  pushWaves(layers, {at: targetAt, hexSize: size, waves: win.waves, colors: [FACTION.infernal, COLORS.heart], startMs: impactMs, hexRotation});
  pushRays(layers, {at: targetGround, size, rays: win.rays, tint: FACTION.infernalLight, startMs: impactMs});
  pushHexFlare(layers, {
    at: targetAt, hexSize: size, tint: FACTION.infernal, lightTint: COLORS.heartLight, startMs: impactMs,
    durationMs: win.seal.durationMs, alpha: win.seal.alpha, hexRotation, pulses: win.seal.pulses,
  });
  for (let index = 0; index < win.tongues.count; index += 1) {
    const angle = -Math.PI / 2 + (index / (win.tongues.count - 1) - 0.5) * 2 * win.tongues.fan + (rng() - 0.5) * 0.14;
    pushStreak(layers, {
      at: to, angle, distance: size * lerp(win.tongues.minReachFactor, win.tongues.maxReachFactor, rng()),
      lengthPx: size * win.tongues.lengthFactor, thicknessPx: size * win.tongues.thicknessFactor,
      tint: [FACTION.infernal, FACTION.infernalLight, COLORS.heart][index % 3], startMs: impactMs, lifeMs: win.tongues.lifeMs, order: 8,
    });
  }
  for (let index = 0; index < win.smoke.count; index += 1) {
    const angle = index / win.smoke.count * TWO_PI + (rng() - 0.5) * 0.6;
    const reach = size * win.smoke.spreadFactor * (0.6 + 0.6 * rng());
    pushPuff(layers, {
      at: {x: to.x, y: to.y + Math.sin(angle) * reach * 0.45}, tint: FACTION.plum, sizePx: size * win.smoke.sizeFactor,
      startMs: impactMs + 30, lifeMs: win.smoke.lifeMs, alpha: win.smoke.alpha, shiftX: Math.cos(angle) * reach, riseY: size * 0.3, spin: (rng() - 0.5) * 0.9, order: 2,
    });
  }
  const spiral = [];
  pushRisingSpiral(spiral, {
    centerX: to.x, groundY: targetGround.y, topY: targetAt.y - size * win.stream.topLiftFactor, size, particles: win.stream, order: 6, rng,
    tints: [COLORS.heart, FACTION.infernalLight, COLORS.heartLight, FACTION.infernal],
  });
  spiral.forEach((element) => {
    layers.push({...element, startMs: element.startMs + impactMs, endMs: element.endMs + impactMs});
  });

  return withLayers(plan, layers, {atMs: impactMs, durationMs: win.shake.durationMs, intensity: win.shake.intensity});
};

/**
 * БОМБА ГОБЛИНОВ: прицел на цели, огненная комета и шестиугольные волны взрыва.
 * Все расходится по кругу: взрыв виден сверху, как и все поле
 *
 * @param {Object} params — как у buildGoblinsBombCastPlan
 * @param {number} [params.hexRotation]
 */
export const buildGoblinsBombRichCastPlan = ({attacker, target, hexSize, outcome, hexRotation = 0, rng = Math.random}) => {
  const plan = buildGoblinsBombCastPlan({attacker, target, hexSize, outcome, rng});
  const {windup, flight} = GOBLINS_BOMB_CAST;
  const {sight, throwRing, blast, destroyed, deflected} = GOBLINS_BOMB_RICH_CAST;
  const size = hexSize;
  const {impactMs} = plan;
  const launch = {x: attacker.body.x, y: attacker.body.y - size * windup.liftFactor};
  const to = target.center || target.body;
  const lift = resolveArcLift(Math.hypot(to.x - launch.x, to.y - launch.y), size, flight);
  const layers = [];

  layers.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: FX.fireLight, sizePx: size * throwRing.sizeFactor,
    startMs: flight.startMs, durationMs: throwRing.durationMs, layer: 'ground',
    pose: (local) => ({...(attacker.ground || attacker.body), scale: 0.2 + 0.8 * easeOutCubic(local), alpha: throwRing.alpha * (1 - local)}),
  }));
  pushClosingSight(layers, {at: to, hexSize: size, tint: FX.target, sight, impactMs, hexRotation});
  // Ячейка цели наливается красным, пока бомба летит
  layers.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: FX.target, sizePx: resolveHexSizePx(size),
    startMs: Math.max(0, impactMs - sight.leadMs), durationMs: Math.min(impactMs, sight.leadMs), layer: 'ground', order: 1,
    pose: (local) => ({...to, rotation: hexRotation, alpha: sight.fillAlpha + (1 - sight.fillAlpha) * local * local}),
  }));
  pushComet(layers, {
    pointAt: (share) => resolveArcPoint(launch, to, lift, share), startMs: flight.startMs, durationMs: flight.durationMs, size, tints: [FX.fire, FX.fireLight],
  });

  if (outcome === GOBLINS_BOMB_OUTCOMES.FAILED) {
    return withLayers(plan, layers);
  }

  if (outcome === GOBLINS_BOMB_OUTCOMES.DEFLECTED) {
    pushHexFlare(layers, {
      at: to, hexSize: size, tint: FACTION.shieldDeep, lightTint: FX.steel, startMs: impactMs,
      durationMs: deflected.shield.durationMs, alpha: deflected.shield.alpha, hexRotation, sizeFactor: deflected.shield.sizeFactor,
    });
    pushRadialStreaks(layers, {at: to, size, streaks: deflected.streaks, tints: [FX.steel, FX.fireLight], startMs: impactMs, rng});
    return withLayers(plan, layers);
  }

  const isDestroyed = outcome === GOBLINS_BOMB_OUTCOMES.DESTROYED;
  const waves = isDestroyed ? [...blast.waves, destroyed.extraWave] : blast.waves;
  pushWaves(layers, {at: to, hexSize: size, waves, colors: [FX.fire, FX.fireLight, FX.target], startMs: impactMs, hexRotation});
  pushRays(layers, {at: to, size, rays: blast.rays, tint: FX.fire, startMs: impactMs});
  pushRadialStreaks(layers, {
    at: to, size, streaks: blast.streaks, tints: [FX.fireCore, FX.fireLight, FX.fire], startMs: impactMs,
    count: Math.round(blast.streaks.count * (isDestroyed ? destroyed.streaksScale : 1)),
    reachScale: isDestroyed ? destroyed.reachScale : 1, rng,
  });
  pushHexFlare(layers, {at: to, hexSize: size, tint: FX.fire, lightTint: FX.fireLight, startMs: impactMs, durationMs: blast.ember.durationMs, alpha: blast.ember.alpha, hexRotation, order: 0});

  return withLayers(plan, layers);
};

/**
 * ЛОВУШКА ГОБЛИНОВ: капкан вращается над ячейкой и защелкивается на ней
 *
 * @param {Object} params — как у buildGoblinsTrapCastPlan
 * @param {number} [params.hexRotation]
 */
export const buildGoblinsTrapRichCastPlan = ({attacker, target, hexSize, success, hexRotation = 0, rng = Math.random}) => {
  const plan = buildGoblinsTrapCastPlan({attacker, target, hexSize, success, rng});
  const {flight} = GOBLINS_TRAP_CAST;
  const {jaws, success: win, failure: lose} = GOBLINS_TRAP_RICH_CAST;
  const size = hexSize;
  const {impactMs} = plan;
  const to = target.outlineCenter || target.body;
  const layers = [];

  pushCharge(layers, {
    anchor: attacker, size, color: FACTION.goblinGlow, lightColor: COLORS.goblinsLight, launchMs: flight.startMs, endMs: impactMs, hexRotation, rng,
  });
  pushComet(layers, {
    pointAt: flightOf(attacker.body, to, size, flight), startMs: flight.startMs, durationMs: flight.durationMs, size, tints: [FACTION.goblinGlow, COLORS.goblinsDark],
  });
  pushClosingSight(layers, {at: to, hexSize: size, tint: FACTION.goblinGlow, sight: jaws, impactMs, hexRotation, tailMs: 80});

  if (!success) {
    pushHexWave(layers, {at: to, hexSize: size, color: COLORS.failGray, endFactor: lose.wave.endFactor, durationMs: lose.wave.durationMs, alpha: lose.wave.alpha, startMs: impactMs, hexRotation});
    return withLayers(plan, layers);
  }

  pushFlash(layers, {at: to, tint: COLORS.goblinsLight, sizePx: size * win.flash.sizeFactor, startMs: impactMs, durationMs: win.flash.durationMs, order: 9, blend: 'add'});
  [CELL_OUTLINE_TEXTURE_KEYS.GLOW, HEX_WAVE_TEXTURE_KEYS.CORE].forEach((textureKey, index) => {
    layers.push(createElement({
      textureKey, tint: index ? COLORS.goblinsLight : FACTION.goblinGlow, sizePx: resolveHexSizePx(size),
      startMs: impactMs - win.inward.durationMs, durationMs: win.inward.durationMs, order: 5 + index,
      pose: (local) => ({
        ...to,
        scale: lerp(win.inward.fromFactor, win.inward.toFactor, local * local),
        rotation: hexRotation,
        alpha: win.inward.alpha * clamp01(local * 3),
      }),
    }));
  });
  // Зубья капкана бьют от краев к центру
  for (let index = 0; index < win.teeth.count; index += 1) {
    const angle = hexRotation + index / win.teeth.count * TWO_PI;
    const reach = size * win.teeth.reachFactor;
    pushStreak(layers, {
      at: {x: to.x + Math.cos(angle) * reach, y: to.y + Math.sin(angle) * reach}, angle: angle + Math.PI, distance: reach * 0.8,
      lengthPx: size * win.teeth.lengthFactor, thicknessPx: size * win.teeth.thicknessFactor,
      tint: index % 2 ? COLORS.goblinsLight : FACTION.goblinGlow, startMs: impactMs, lifeMs: win.teeth.lifeMs, order: 9,
    });
  }
  pushHexFlare(layers, {at: to, hexSize: size, tint: FACTION.goblinGlow, lightTint: COLORS.goblinsLight, startMs: impactMs, durationMs: win.pulse.durationMs, alpha: win.pulse.alpha, hexRotation, pulses: win.pulse.count});
  pushRays(layers, {at: to, size, rays: win.rays, tint: FACTION.goblinGlow, startMs: impactMs});

  return withLayers(plan, layers);
};

/**
 * ЛОВУШКА ГЛАЗАМИ СОПЕРНИКОВ: все только вокруг самого гоблина, ячейку не выдает
 *
 * @param {Object} params — как у buildGoblinsHiddenTrapCastPlan
 * @param {number} [params.hexRotation]
 */
export const buildGoblinsHiddenTrapRichCastPlan = ({attacker, hexSize, success, hexRotation = 0, rng = Math.random}) => {
  const plan = buildGoblinsHiddenTrapCastPlan({attacker, hexSize, success, rng});
  const {hidden} = GOBLINS_TRAP_RICH_CAST;
  const size = hexSize;
  const {impactMs} = plan;
  const at = attacker.center || attacker.body;
  const layers = [];

  pushRune(layers, {at, hexSize: size, tint: FACTION.goblinGlow, radiusFactor: CHARGE.rune.radiusFactor, turn: CHARGE.rune.turn, durationMs: impactMs + 160, alpha: CHARGE.rune.alpha, hexRotation});
  if (success) {
    pushRays(layers, {at: attacker.ground || at, size, rays: hidden.rays, tint: FACTION.goblinGlow, startMs: impactMs});
    pushHexWave(layers, {at, hexSize: size, color: FACTION.goblinGlow, endFactor: hidden.wave.endFactor, durationMs: hidden.wave.durationMs, alpha: hidden.wave.alpha, startMs: impactMs, hexRotation});
  }

  return withLayers(plan, layers);
};

/**
 * КРАЖА ЛЕПРЕКОНОВ: фонтан монет, изумрудный столб и золотые волны вокруг вора. Момент
 * итога остается в самом начале: монеты уже у него
 *
 * @param {Object} params — как у buildLeprechaunsTheftCastPlan
 * @param {number} [params.hexRotation]
 */
export const buildLeprechaunsTheftRichCastPlan = ({unit, hexSize, hexRotation = 0, rng = Math.random}) => {
  const plan = buildLeprechaunsTheftCastPlan({unit, hexSize, rng});
  const {flash, waves, rune, rays, beam, coins} = LEPRECHAUNS_RICH_CAST;
  const size = hexSize;
  const {center, ground} = unit;
  const layers = [];

  pushFlash(layers, {at: center, tint: COLORS.upgradeLight, sizePx: size * flash.sizeFactor, durationMs: flash.durationMs, order: 9, blend: 'add'});
  pushWaves(layers, {at: center, hexSize: size, waves, colors: [COLORS.leprechaunGold, COLORS.leprechaunGreen], startMs: 0, hexRotation});
  pushRune(layers, {at: center, hexSize: size, tint: COLORS.leprechaunGold, radiusFactor: rune.radiusFactor, turn: rune.turn, durationMs: rune.durationMs, alpha: rune.alpha, hexRotation});
  pushRune(layers, {at: center, hexSize: size, tint: COLORS.leprechaunMint, radiusFactor: rune.innerRadiusFactor, turn: -rune.turn, durationMs: rune.durationMs, alpha: rune.alpha, hexRotation, order: 2});
  pushRays(layers, {at: ground, size, rays, tint: COLORS.leprechaunGold, startMs: 0});

  const beamHeight = size * beam.heightFactor;
  [
    {widthPx: size * beam.widthFactor, tint: COLORS.leprechaunGreen, order: 1},
    {widthPx: size * beam.widthFactor * beam.coreWidthShare, tint: COLORS.leprechaunMint, order: 1.5},
  ].forEach(({widthPx, tint, order}) => {
    layers.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx, startMs: 0, durationMs: beam.durationMs, order,
      pose: (local) => {
        const height = beamHeight * lerp(0.35, 1, easeOutCubic(clamp01(local * 2)));
        return {
          x: center.x,
          y: ground.y - height / 2,
          scaleX: lerp(1, 0.2, local * local),
          scaleY: height / (widthPx * LIGHT_BEAM_ASPECT),
          alpha: fadeInOut(local, 0.12, 0.5),
        };
      },
    }));
  });

  // Монета вращается на ребро: ширина идет по косинусу
  for (let index = 0; index < coins.count; index += 1) {
    const side = index % 2 ? 1 : -1;
    const spread = size * lerp(coins.minSpreadFactor, coins.maxSpreadFactor, rng()) * side;
    const liftPx = size * lerp(coins.minLiftFactor, coins.maxLiftFactor, rng());
    const spins = lerp(coins.minSpins, coins.maxSpins, rng());
    const phase = rng() * TWO_PI;
    layers.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.COIN, sizePx: size * coins.sizeFactor,
      startMs: index / coins.count * coins.spawnMs, durationMs: coins.lifeMs, order: 7,
      pose: (local) => ({
        x: center.x + spread * easeOutQuad(local),
        y: ground.y - liftPx * 4 * local * (1 - local),
        scaleX: Math.max(0.12, Math.abs(Math.cos(phase + local * TWO_PI * spins))),
        scaleY: 1,
        alpha: fadeInOut(local, 0.08, 0.2),
      }),
    }));
  }

  return withLayers(plan, layers);
};
